import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Box, Grid, TextField, Button, Typography, useTheme, alpha } from '@mui/material'
import { Send, CheckCircle } from '@mui/icons-material'
import { API_BASE } from '../lib/config'
import { useTranslation } from 'react-i18next'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'

interface ContactFormData {
  name: string
  email: string
  subject: string
  message: string
  company?: string
  phone?: string
}

const ContactForm: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isDarkMode = theme.palette.mode === 'dark'
  const [sending, setSending] = React.useState(false)
  const { toastPromise } = useToast()
  const toastMessages = useToastMessages()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
      company: '',
      phone: ''
    },
    mode: 'onBlur'
  })

  const onSubmit = async (data: ContactFormData) => {
    setSending(true)
    const promise = fetch(`${API_BASE}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, captcha: '' })
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error((await res.text()) || t('sendError'))
      }
      return res
    })

    toastPromise(promise, {
      loading: toastMessages.sending(),
      success: () => {
        reset()
        return toastMessages.contactThanks24h()
      },
      error: (err: unknown) => {
        const error = err as Error
        return error?.message || toastMessages.sendFailed()
      },
      onFinally: () => {
        setSending(false)
      },
    })
  }

  const watchedFields = watch()

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      color: isDarkMode ? theme.palette.common.white : theme.palette.text.primary,
      backgroundColor: isDarkMode ? alpha('#ffffff', 0.06) : theme.palette.background.paper,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main
      },
      '& input::placeholder, & textarea::placeholder': {
        color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.secondary, 0.8),
        opacity: 1
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : alpha(theme.palette.text.primary, 0.2)
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : alpha(theme.palette.text.primary, 0.7),
      '&.Mui-focused': {
        color: theme.palette.primary.main
      }
    },
    '& .MuiFormHelperText-root': {
      color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.secondary, 0.8)
    }
  }

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: t('nameRequired'),
                minLength: {
                  value: 2,
                  message: t('nameMin2')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t('name')} *`}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('invalidEmail')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  label={`${t('email')} *`}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="company"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('company')}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="phone"
              control={control}
              rules={{
                pattern: {
                  value: /^[\+]?[0-9\s\-\(\)]{10,}$/,
                  message: t('invalidPhone')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="subject"
              control={control}
              rules={{
                required: t('subjectRequired'),
                minLength: {
                  value: 3,
                  message: t('subjectMin3')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t('subject')} *`}
                  error={!!errors.subject}
                  helperText={errors.subject?.message}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="message"
              control={control}
              rules={{
                required: t('messageRequired'),
                minLength: {
                  value: 10,
                  message: t('messageMin10')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={5}
                  label={`${t('message')} *`}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={sending || isSubmitting}
                endIcon={sending ? <CheckCircle /> : <Send />}
                sx={{
                  py: 2,
                  px: 4,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: 200,
                  '&:hover': {
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.primary.main, 0.3),
                    color: isDarkMode
                      ? 'rgba(255, 255, 255, 0.7)'
                      : alpha(theme.palette.text.primary, 0.6)
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {sending ? t('sending') : t('send')}
              </Button>

              {Object.values(watchedFields).some(value => value && value.length > 0) && (
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'italic'
                  }}
                >
                  {t('allFieldsRequired')}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default ContactForm

